import React, { useContext, useEffect, useState } from "react";
import CurrencyList from "currency-list";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import NotyfContext from "../../../../contexts/NotyfContext";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import useWindowSize from "../../../../hooks/useWindowSize";
import { getPortfolioInvoices } from "../../../../redux/slices/invoices";
import { Modal, Offcanvas } from "react-bootstrap";
import CreditNoteForm from "./CreditNoteForm";
import { issueCreditNote } from "../../../../redux/slices/creditNotes";
import useDeviceType from "../../../../hooks/useDeviceType";

const CreditNoteModal = ({
  showCreditNoteModal,
  setShowCreditNoteModal,
  client,
}) => {
  const { backgroundColor, fontColor } = useWhitelabel();
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const { t } = useTranslation();
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(showCreditNoteModal);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (showCreditNoteModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [showCreditNoteModal]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setShowCreditNoteModal(false);
    }, 300);
  };

  const initialValues = {
    amount: "",
    vat: "",
    reason: "",
  };

  const validation = Yup.object().shape({
    amount: Yup.number()
      .min(0, "Must be a positive number")
      .required("Required"),
    vat: Yup.number().min(0, "Must be a positive number").required("Required"),
    reason: Yup.string().required("Required"),
  });

  if (isDesktop) {
    return (
      <Modal
        show={showCreditNoteModal}
        onHide={() => setShowCreditNoteModal(false)}
        centered
        scrollable
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("crm.creditNote.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              const data = {
                clientId: client.id,
                amount: values.amount,
                vat: values.vat,
                reason: values.reason,
              };
              await dispatch(issueCreditNote({ data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Credit Note issued successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setShowCreditNoteModal(false);
                  setValues(initialValues);
                });
            }}
          >
            {({
              errors,
              handleBlur,
              handleSubmit,
              handleChange,
              touched,
              values,
              setValues,
              setFieldValue,
              setFieldTouched,
            }) => {
              return (
                <CreditNoteForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setValues={setValues}
                  setShowCreditNoteModal={setShowCreditNoteModal}
                  initialValues={initialValues}
                  handleSubmit={handleSubmit}
                />
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        backdrop="static"
        scrollable={true}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
        onHide={handleHide}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>{t("crm.creditNote.title")}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              const data = {
                clientId: client.id,
                amount: values.amount,
                vat: values.vat,
                reason: values.reason,
              };
              await dispatch(issueCreditNote({ data }))
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "Credit Note issued successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setShowCreditNoteModal(false);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              setFieldTouched,
              handleSubmit,
              setValues,
            }) => {
              return (
                <CreditNoteForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setValues={setValues}
                  handleSubmit={handleSubmit}
                />
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default CreditNoteModal;
